import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, Loader2, RefreshCw, Unlink, X, Plus, ArrowUpRight, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { storeChannelMeta } from './channelMeta';
import { StatusPill } from './StatusPill';
import { CountBadge } from './CountBadge';
import { itemVariants } from './motion';
import type { EcommerceConnection } from '@/types/api';
import { useShopifyInstallUrl } from '@/features/shopify/useShopifyInstallUrl';

interface StoreConnectionCardProps {
  platform: 'shopify' | 'woocommerce' | 'amazon' | 'salla' | 'ebay' | 'lazada';
  connections: EcommerceConnection[];
  onConnectShopify: (shop: string) => void;
  onConnectWooCommerce: (data: { siteUrl: string; consumerKey: string; consumerSecret: string }) => Promise<unknown>;
  onConnectAmazon?: (data: { sellerId: string; clientId: string; clientSecret: string; refreshToken: string; marketplaceId?: string }) => Promise<unknown>;
  onConnectSalla?: (data: { accessToken: string }) => Promise<unknown>;
  onConnectEbay?: (data: { accessToken: string }) => Promise<unknown>;
  onConnectLazada?: (data: { accessToken: string; appKey: string }) => Promise<unknown>;
  onSync: (connectionId: string) => void;
  onDisconnect: (connectionId: string) => void;
  isConnecting?: boolean;
  isSyncing?: boolean;
  isDisconnecting?: boolean;
}

/**
 * Commerce-store card (Shopify / WooCommerce / Amazon / Salla / eBay / Lazada) with uniform collapsed height and expandable store management.
 */
export const StoreConnectionCard: React.FC<StoreConnectionCardProps> = ({
  platform,
  connections,
  onConnectShopify,
  onConnectWooCommerce,
  onConnectAmazon,
  onConnectSalla,
  onConnectEbay,
  onConnectLazada,
  onSync,
  onDisconnect,
  isConnecting = false,
  isSyncing = false,
  isDisconnecting = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const meta = storeChannelMeta[platform];
  const Icon = meta.icon;
  const isConnected = connections.length > 0;
  const needsAttention = isConnected && connections.some((c) => c.syncStatus === 'error' || !c.isActive);

  const [formOpen, setFormOpen] = useState(false);
  const [shop, setShop] = useState('');
  const [wooForm, setWooForm] = useState({ siteUrl: '', consumerKey: '', consumerSecret: '' });
  const [amazonForm, setAmazonForm] = useState({ sellerId: '', clientId: '', clientSecret: '', refreshToken: '', marketplaceId: 'ATVPDKIKX0DER' });
  const [singleTokenForm, setSingleTokenForm] = useState({ accessToken: '', appKey: '' });
  const [submitting, setSubmitting] = useState(false);
  const shopifyInstallUrl = useShopifyInstallUrl();

  const handleShopifySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!shop.trim()) return;
    onConnectShopify(shop.trim());
  };

  const handleWooSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wooForm.siteUrl || !wooForm.consumerKey || !wooForm.consumerSecret) return;
    setSubmitting(true);
    try {
      await onConnectWooCommerce(wooForm);
      setFormOpen(false);
      setWooForm({ siteUrl: '', consumerKey: '', consumerSecret: '' });
    } catch {
      // Error toast is handled by mutation
    } finally {
      setSubmitting(false);
    }
  };

  const handleAmazonSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amazonForm.sellerId || !amazonForm.clientId || !amazonForm.clientSecret || !amazonForm.refreshToken) return;
    if (!onConnectAmazon) return;
    setSubmitting(true);
    try {
      await onConnectAmazon(amazonForm);
      setFormOpen(false);
      setAmazonForm({ sellerId: '', clientId: '', clientSecret: '', refreshToken: '', marketplaceId: 'ATVPDKIKX0DER' });
    } catch {
      // Error toast is handled by mutation
    } finally {
      setSubmitting(false);
    }
  };

  const handleCustomStoreSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!singleTokenForm.accessToken) return;
    setSubmitting(true);
    try {
      if (platform === 'salla' && onConnectSalla) {
        await onConnectSalla({ accessToken: singleTokenForm.accessToken });
      } else if (platform === 'ebay' && onConnectEbay) {
        await onConnectEbay({ accessToken: singleTokenForm.accessToken });
      } else if (platform === 'lazada' && onConnectLazada) {
        await onConnectLazada({ accessToken: singleTokenForm.accessToken, appKey: singleTokenForm.appKey });
      }
      setFormOpen(false);
      setSingleTokenForm({ accessToken: '', appKey: '' });
    } catch {
      // Handled by mutation
    } finally {
      setSubmitting(false);
    }
  };

  const storeNames = connections.map((c) => c.storeName || c.storeUrl).join(', ');

  return (
    <motion.div variants={itemVariants} className="h-full">
      <Card className="flex h-full max-w-none flex-col overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs transition-all hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-sm">
        {/* Pod Header */}
        <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800 bg-slate-100/50 dark:bg-slate-850/60 px-3.5 py-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-lg shadow-2xs', meta.classes.iconBg)}>
              <Icon className={cn('h-3.5 w-3.5', meta.classes.iconText)} />
            </div>
            <h3 className="truncate text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
              {meta.name}
            </h3>
            <StatusPill
              connected={isConnected}
              needsAttention={needsAttention}
              tooltipText={
                needsAttention
                  ? `${meta.name}: Sync issue or re-authorization required`
                  : isConnected
                    ? `${meta.name}: Connected & Synced (${connections.length} store${connections.length > 1 ? 's' : ''})`
                    : `${meta.name}: Not connected`
              }
            />
            {isConnected && connections.length > 0 && (
              <CountBadge
                count={connections.length}
                tooltipText={`${connections.length} active ${meta.name} store${connections.length > 1 ? 's' : ''}: ${storeNames}`}
                variant={needsAttention ? 'amber' : 'neutral'}
              />
            )}
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {!formOpen ? (
              isConnected ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="h-7 px-2 rounded-lg text-[11px] font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-800 transition-colors gap-1"
                    title={isExpanded ? 'Collapse stores' : 'Manage stores'}
                  >
                    <span>{isExpanded ? 'Hide' : 'Manage'}</span>
                    <ChevronDown className={cn('h-3 w-3 transition-transform duration-200', isExpanded && 'rotate-180')} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isConnecting}
                    onClick={() =>
                      platform === 'shopify' && shopifyInstallUrl
                        ? window.open(shopifyInstallUrl, '_blank', 'noopener,noreferrer')
                        : setFormOpen(true)
                    }
                    className="h-7 px-2 rounded-lg border-slate-200/80 dark:border-slate-700 bg-white dark:bg-slate-800 text-[11px] font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors gap-1 shadow-2xs"
                    title="Add another store"
                  >
                    <Plus className="h-3 w-3" />
                    <span>Add</span>
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() =>
                    platform === 'shopify' && shopifyInstallUrl
                      ? window.open(shopifyInstallUrl, '_blank', 'noopener,noreferrer')
                      : setFormOpen(true)
                  }
                  disabled={isConnecting}
                  size="sm"
                  className={cn('h-7 px-3 rounded-lg text-xs font-semibold shadow-2xs transition-colors gap-1.5', meta.classes.connectBtn)}
                >
                  {isConnecting ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowUpRight className="h-3 w-3" />}
                  <span>Connect</span>
                </Button>
              )
            ) : null}
          </div>
        </div>

        {/* Pod Body */}
        <div className="flex flex-1 flex-col justify-between p-3 bg-white/70 dark:bg-slate-900/70">
          <div className="space-y-2">
            <p className="line-clamp-1 text-xs text-slate-500 dark:text-slate-400">{meta.description}</p>

            {/* Collapsed summary line */}
            {isConnected ? (
              !isExpanded && (
                <div className="flex items-center justify-between rounded-xl border border-slate-200/70 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-850/60 px-2.5 py-1.5 text-[11px]">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', needsAttention ? 'bg-amber-500' : 'bg-emerald-500')} />
                    <span className="truncate font-semibold text-slate-800 dark:text-slate-200">
                      {connections[0]?.storeName || connections[0]?.storeUrl}
                    </span>
                    {connections.length > 1 && (
                      <span className="text-slate-400 dark:text-slate-500 font-normal">+{connections.length - 1} more</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsExpanded(true)}
                    className="text-[11px] font-semibold text-primary hover:underline ml-2 shrink-0"
                  >
                    Details
                  </button>
                </div>
              )
            ) : (
              <div className="flex items-center gap-1.5 rounded-xl border border-slate-200/60 dark:border-slate-800/80 bg-slate-50/40 dark:bg-slate-850/30 px-2.5 py-1.5 text-[11px] text-slate-400 dark:text-slate-500">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-300 dark:bg-slate-600 shrink-0" />
                <span>No store connected</span>
              </div>
            )}

            {/* Expanded store management tray */}
            <AnimatePresence initial={false}>
              {isConnected && isExpanded && (
                <motion.div
                  key="store-list"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-1.5 overflow-hidden pt-1"
                >
                  {connections.map((conn) => (
                    <div
                      key={conn.id}
                      className="flex items-center justify-between gap-2 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 px-2.5 py-1.5 shadow-2xs"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-bold text-slate-800 dark:text-slate-200">{conn.storeName || conn.storeUrl}</p>
                        <p className="truncate text-[10px] text-slate-400 dark:text-slate-500 font-medium">
                          {conn.syncStatus === 'syncing'
                            ? 'Syncing…'
                            : conn.syncStatus === 'error'
                              ? 'Sync failed'
                              : conn.lastSync
                                ? `Synced ${new Date(conn.lastSync).toLocaleDateString()}`
                                : 'Not synced yet'}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[11px] font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg gap-1"
                          onClick={() => onSync(conn.id)}
                          disabled={isSyncing || conn.syncStatus === 'syncing'}
                          title="Sync now"
                          aria-label={`Sync ${conn.storeName || conn.storeUrl}`}
                        >
                          <RefreshCw className={cn('h-3 w-3', conn.syncStatus === 'syncing' && 'animate-spin text-primary')} />
                          <span>Sync</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-[11px] font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg gap-1"
                          onClick={() => onDisconnect(conn.id)}
                          disabled={isDisconnecting}
                          title="Disconnect"
                          aria-label={`Disconnect ${conn.storeName || conn.storeUrl}`}
                        >
                          {isDisconnecting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Unlink className="h-3 w-3" />}
                          <span>Disconnect</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Connect Store Form Modal / Inline Box */}
          <AnimatePresence initial={false}>
            {formOpen && (
              <motion.form
                key="form"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                onSubmit={
                  platform === 'shopify'
                    ? handleShopifySubmit
                    : platform === 'amazon'
                      ? handleAmazonSubmit
                      : platform === 'woocommerce'
                        ? handleWooSubmit
                        : handleCustomStoreSubmit
                }
                className="mt-2.5 pt-2 border-t border-slate-100 dark:border-slate-800 space-y-2 overflow-hidden"
              >
                {platform === 'shopify' ? (
                  <div>
                    <Label htmlFor="shopify-shop" className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">
                      Store domain
                    </Label>
                    <Input
                      id="shopify-shop"
                      value={shop}
                      onChange={(e) => setShop(e.target.value)}
                      placeholder="your-store.myshopify.com"
                      className="mt-1 h-8.5 rounded-xl text-xs"
                      autoFocus
                    />
                  </div>
                ) : platform === 'amazon' ? (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="amz-seller" className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">
                          Seller ID
                        </Label>
                        <Input
                          id="amz-seller"
                          value={amazonForm.sellerId}
                          onChange={(e) => setAmazonForm((prev) => ({ ...prev, sellerId: e.target.value }))}
                          placeholder="A2..."
                          className="mt-1 h-8.5 rounded-xl text-xs"
                          autoFocus
                        />
                      </div>
                      <div>
                        <Label htmlFor="amz-market" className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">
                          Marketplace ID
                        </Label>
                        <Input
                          id="amz-market"
                          value={amazonForm.marketplaceId}
                          onChange={(e) => setAmazonForm((prev) => ({ ...prev, marketplaceId: e.target.value }))}
                          placeholder="ATVPDKIKX0DER (US)"
                          className="mt-1 h-8.5 rounded-xl text-xs"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="amz-client" className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">
                          LWA Client ID
                        </Label>
                        <Input
                          id="amz-client"
                          value={amazonForm.clientId}
                          onChange={(e) => setAmazonForm((prev) => ({ ...prev, clientId: e.target.value }))}
                          placeholder="amzn1.application-oa2-client..."
                          className="mt-1 h-8.5 rounded-xl text-xs"
                        />
                      </div>
                      <div>
                        <Label htmlFor="amz-secret" className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">
                          LWA Client Secret
                        </Label>
                        <Input
                          id="amz-secret"
                          type="password"
                          value={amazonForm.clientSecret}
                          onChange={(e) => setAmazonForm((prev) => ({ ...prev, clientSecret: e.target.value }))}
                          placeholder="amzn1.oa2-cs.v1..."
                          className="mt-1 h-8.5 rounded-xl text-xs"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="amz-refresh" className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">
                        LWA Refresh Token
                      </Label>
                      <Input
                        id="amz-refresh"
                        type="password"
                        value={amazonForm.refreshToken}
                        onChange={(e) => setAmazonForm((prev) => ({ ...prev, refreshToken: e.target.value }))}
                        placeholder="Atzr|..."
                        className="mt-1 h-8.5 rounded-xl text-xs"
                      />
                    </div>
                  </>
                ) : platform === 'woocommerce' ? (
                  <>
                    <div>
                      <Label htmlFor="woo-url" className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">
                        Store URL
                      </Label>
                      <Input
                        id="woo-url"
                        value={wooForm.siteUrl}
                        onChange={(e) => setWooForm((prev) => ({ ...prev, siteUrl: e.target.value }))}
                        placeholder="https://yourstore.com"
                        className="mt-1 h-8.5 rounded-xl text-xs"
                        autoFocus
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="woo-key" className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">
                          Consumer key
                        </Label>
                        <Input
                          id="woo-key"
                          value={wooForm.consumerKey}
                          onChange={(e) => setWooForm((prev) => ({ ...prev, consumerKey: e.target.value }))}
                          placeholder="ck_..."
                          className="mt-1 h-8.5 rounded-xl text-xs"
                        />
                      </div>
                      <div>
                        <Label htmlFor="woo-secret" className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">
                          Consumer secret
                        </Label>
                        <Input
                          id="woo-secret"
                          type="password"
                          value={wooForm.consumerSecret}
                          onChange={(e) => setWooForm((prev) => ({ ...prev, consumerSecret: e.target.value }))}
                          placeholder="cs_..."
                          className="mt-1 h-8.5 rounded-xl text-xs"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="token-auth" className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">
                        {platform === 'salla' ? 'Salla Access Token' : platform === 'ebay' ? 'eBay User OAuth Token' : 'Lazada Access Token'}
                      </Label>
                      <Input
                        id="token-auth"
                        type="password"
                        value={singleTokenForm.accessToken}
                        onChange={(e) => setSingleTokenForm((prev) => ({ ...prev, accessToken: e.target.value }))}
                        placeholder={platform === 'salla' ? 'Bearer token...' : platform === 'ebay' ? 'v^1.1#i^1#...' : '5000...'}
                        className="mt-1 h-8.5 rounded-xl text-xs"
                        autoFocus
                      />
                    </div>
                    {platform === 'lazada' && (
                      <div>
                        <Label htmlFor="lazada-appkey" className="text-[10px] font-semibold text-slate-600 dark:text-slate-300">
                          Lazada App Key
                        </Label>
                        <Input
                          id="lazada-appkey"
                          value={singleTokenForm.appKey}
                          onChange={(e) => setSingleTokenForm((prev) => ({ ...prev, appKey: e.target.value }))}
                          placeholder="123456"
                          className="mt-1 h-8.5 rounded-xl text-xs"
                        />
                      </div>
                    )}
                  </>
                )}
                <div className="flex gap-1.5 pt-0.5">
                  <Button
                    type="submit"
                    size="sm"
                    disabled={isConnecting || submitting}
                    className={cn('h-8 flex-1 rounded-xl text-xs font-semibold shadow-2xs gap-1.5', meta.classes.connectBtn)}
                  >
                    {isConnecting || submitting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <ArrowRight className="h-3.5 w-3.5" />
                    )}
                    Connect
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                    onClick={() => setFormOpen(false)}
                    aria-label="Cancel"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </Card>
    </motion.div>
  );
};

export default StoreConnectionCard;
